import { css } from "lit";
import { baseStyles } from "../../theme/theme";

export const appointmentsStyles = [
  baseStyles,
  css`
    .wrap {
      max-width: 960px;
      margin: 0 auto;
      padding: 1.25rem 1.25rem 2.5rem;
    }
    .head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      flex-wrap: wrap;
      margin-bottom: 0.5rem;
    }
    /* 7/25 QA: the sign-in/out control is the shared '.zui-btn-secondary'
         outline button from 'theme/theme.ts' — the same treatment the events
         register flow and both checkouts now use — so "Sign in" reads as a
         button everywhere instead of accent-colored text here and a button
         there. '.signin' is kept purely as a stable hook for the AUTH-040…042
         presentation lock; it deliberately declares nothing, because any
         property it set would win over the shared class (component styles are
         listed after 'baseStyles') and silently fork the treatment again. */
    .tz-pill {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.78rem;
      color: var(--zui-panel-ink-soft);
      min-width: 0;
      flex: 0 1 auto;
    }
    /* D1: symmetric padding + text-align (both properties needed — Firefox
         ignores text-align on a <select>'s own value unless text-align-last
         is also set) horizontally centers the pill's label instead of it
         sitting left-of-center against the leftover caret gutter. min-width:
         0 lets it shrink at narrow widths so it never overlaps/overflows the
         adjacent Today button. */
    .tz-pill select {
      appearance: none;
      border: 1px solid var(--zui-line);
      border-radius: 999px;
      background: #fff;
      color: var(--zui-headline);
      font: inherit;
      font-size: 0.78rem;
      padding: 0.25rem 0.75rem;
      outline: none;
      text-align: center;
      text-align-last: center;
      min-width: 0;
      max-width: 100%;
      text-overflow: ellipsis;
    }
    .tz-pill select:focus-visible {
      outline: none;
    }
    .crumbs {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.78rem;
      color: var(--zui-panel-ink-soft);
      margin-bottom: 1rem;
    }
    .crumbs .on {
      color: var(--zui-headline);
      font-weight: 700;
    }
    .back {
      appearance: none;
      background: none;
      border: 0;
      color: var(--zui-accent);
      cursor: pointer;
      font: inherit;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      padding: 0;
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      margin-bottom: 0.75rem;
    }
    h2.title {
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 1.5rem;
      letter-spacing: -0.01em;
      color: var(--zui-headline);
      margin: 0 0 1rem;
    }
    .cat {
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--zui-panel-ink-soft);
      margin: 1.25rem 0 0.5rem;
    }
    .svc {
      display: flex;
      gap: 1rem;
      align-items: center;
      flex-wrap: wrap;
      border: 1px solid var(--zui-line);
      /* X2: "square" flattens the first-screen appointment cards too;
           rounded/pill/unset keep the 12px radius via the var() fallback. */
      border-radius: var(--zui-square-radius, 12px);
      background: var(--zui-panel, #fff);
      padding: 0.875rem;
      margin-bottom: 0.6rem;
      transition: border-color 0.15s ease;
    }
    .svc:hover {
      border-color: var(--zui-accent);
    }
    /* Card is a container; this inner button is the service selector so the
         expand caret can live beside it without nesting interactive elements. */
    .svc-main {
      display: flex;
      gap: 1rem;
      align-items: center;
      flex: 1 1 auto;
      min-width: 0;
      appearance: none;
      background: none;
      border: 0;
      padding: 0;
      margin: 0;
      font: inherit;
      text-align: left;
      color: inherit;
      cursor: pointer;
    }
    /* Description accordion caret (APT-35). */
    .svc-exp {
      appearance: none;
      background: none;
      border: 0;
      cursor: pointer;
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      padding: 0.25rem;
      color: var(--zui-panel-ink-soft);
      transition: transform 0.15s ease;
    }
    .svc-exp.open {
      transform: rotate(180deg);
    }
    .svc-desc {
      flex-basis: 100%;
      font-size: 0.8rem;
      color: var(--zui-panel-ink-soft);
      margin-top: 0.15rem;
      line-height: 1.45;
    }
    /* Collapsible category section header (APT-34). */
    .cat-head {
      appearance: none;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      background: none;
      border: 0;
      cursor: pointer;
      font: inherit;
      color: var(--zui-panel-ink-soft);
      padding: 0.5rem 0.25rem;
      margin: 1.25rem 0 0.5rem;
      border-radius: var(--zui-btn-radius, 10px);
    }
    .cat-head .cat-label {
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
    }
    .cat-head .cat-caret {
      display: inline-flex;
      flex-shrink: 0;
      transition: transform 0.15s ease;
    }
    .cat-head .cat-caret.open {
      transform: rotate(180deg);
    }
    .svc .thumb {
      width: 72px;
      height: 56px;
      /* X2: keep the thumbnail flush with the card corners when squared. */
      border-radius: var(--zui-square-radius, 8px);
      object-fit: cover;
      flex: 0 0 auto;
      background: var(--zui-accent-soft);
      /* The slot is shared by the real image and the glyph placeholder, so it
           stays a stable, filled box either way — the thumbnail never
           collapses/disappears when a service has no photo. */
      display: grid;
      place-items: center;
      overflow: hidden;
      font-size: 1.1rem;
      font-weight: 700;
      color: var(--zui-accent-deep);
      text-transform: uppercase;
    }
    /* AP2: image-less services show a soft photo glyph placeholder, never a
         stray character (e.g. the "6" from "60-Minute ..."). */
    .svc .thumb-ph {
      color: var(--zui-accent-deep);
      opacity: 0.5;
    }
    .svc .nm {
      font-weight: 700;
      font-size: 1rem;
      color: var(--zui-headline);
    }
    .nm-row {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      max-width: 100%;
    }
    .svc .meta {
      font-size: 0.8rem;
      color: var(--zui-panel-ink-soft);
      margin-top: 0.1rem;
    }
    /* PLAT-5624: group + in-group provider rows reuse the '.svc' card so the
         first screen stays visually one list. The trailing chevron is what
         says "this goes deeper" rather than "this books"; 'data-kind' is what
         says it in the DOM (specs/POM read it to tell the row types apart). */
    .svc .go {
      align-self: center;
      flex: 0 0 auto;
      display: inline-flex;
      margin-left: 0.25rem;
      color: var(--zui-panel-ink-soft);
    }
    .svc .thumb.avatar {
      width: 56px;
      border-radius: 999px;
    }
    /* Group › Provider trail above the in-group sub-screens. */
    .gcrumb {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.35rem;
      font-size: 0.82rem;
      color: var(--zui-panel-ink-soft);
      margin-bottom: 0.5rem;
    }
    .gcrumb-link {
      appearance: none;
      background: none;
      border: 0;
      padding: 0;
      font: inherit;
      color: var(--zui-accent);
      cursor: pointer;
    }
    .svc .price {
      margin-left: auto;
      /* AP-PRICE: vertically center the price in the card row (it sat
           top-aligned when the name wrapped or the description caret was
           present). The card centers its cross axis, so match that. */
      align-self: center;
      font-weight: 800;
      font-size: 1.1rem;
      color: var(--zui-headline);
      white-space: nowrap;
    }
    /* providers */
    .providers {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin-bottom: 1rem;
    }
    .prov {
      appearance: none;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-btn-radius, 999px);
      background: #fff;
      font: inherit;
      font-size: 0.85rem;
      padding: 0.3rem 0.8rem 0.3rem 0.3rem;
      cursor: pointer;
      color: var(--zui-headline);
    }
    .prov[aria-pressed="true"] {
      border-color: var(--zui-accent);
      background: var(--zui-accent-soft);
    }
    .prov img,
    .prov .ph {
      width: 28px;
      height: 28px;
      border-radius: 999px;
      object-fit: cover;
      background: var(--zui-accent-soft);
      display: grid;
      place-items: center;
      font-size: 0.75rem;
      font-weight: 700;
      color: var(--zui-accent-deep);
    }
    /* date strip */
    .datestrip {
      display: flex;
      align-items: center;
      gap: 4px;
      background: var(--zui-panel);
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-btn-radius, 14px);
      padding: 0.75rem 0.5rem;
      margin-bottom: 1rem;
    }
    /* Header row above the date strip: month/year range + a "Today" reset
         pill (AP4), mirroring the schedule embed's date-nav affordance. The
         timezone picker (D1) lives in this same row, immediately next to
         Today, rather than stacked on its own row below the strip. */
    .pick-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      margin: 0.25rem 0 0.5rem;
      flex-wrap: wrap;
    }
    .pick-head .range {
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 1rem;
      color: var(--zui-headline);
      min-width: 0;
    }
    /* D1: groups the tz picker + Today pill so they stay adjacent on one
         row and shrink together at narrow widths instead of overlapping. */
    .pick-head-actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      min-width: 0;
      flex: 1 1 auto;
      justify-content: flex-end;
    }
    .today-pill {
      appearance: none;
      flex: 0 0 auto;
      background: #fff;
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-btn-radius, 999px);
      padding: 4px 12px;
      font: inherit;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--zui-accent);
      cursor: pointer;
      white-space: nowrap;
    }
    .today-pill:disabled {
      opacity: 0.45;
      cursor: default;
    }
    .arrow {
      appearance: none;
      flex: 0 0 auto;
      width: 32px;
      height: 32px;
      border-radius: 999px;
      border: 1px solid var(--zui-line);
      background: #fff;
      color: var(--zui-panel-ink);
      display: grid;
      place-items: center;
      cursor: pointer;
    }
    .days {
      flex: 1;
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 4px;
    }
    .day {
      appearance: none;
      background: transparent;
      border: 0;
      padding: 0.4rem 0;
      cursor: pointer;
      border-radius: 10px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      color: var(--zui-panel-ink);
    }
    .day[aria-pressed="true"] {
      background: var(--zui-accent);
      color: var(--zui-btn-text);
    }
    .day.empty {
      opacity: 0.35;
      cursor: default;
    }
    .day .dow {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .day .num {
      font-weight: 700;
      font-size: 15px;
    }
    .slotgroup {
      font-size: 0.74rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--zui-panel-ink-soft);
      margin: 0.75rem 0 0.4rem;
    }
    .slots {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
      gap: 0.4rem;
    }
    .slot-cell {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
    .slot {
      appearance: none;
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-btn-radius, 8px);
      background: #fff;
      color: var(--zui-headline);
      font: inherit;
      font-size: 0.82rem;
      padding: 0.5rem;
      cursor: pointer;
    }
    .slot-buyout {
      font-size: 0.68rem;
      padding: 0.3rem 0.4rem;
      border-color: var(--zui-accent);
      color: var(--zui-accent);
    }
    .slot:hover {
      border-color: var(--zui-accent);
    }
    .none {
      color: var(--zui-panel-ink-soft);
      font-size: 0.9rem;
      padding: 0.5rem 0;
    }
    .book-err {
      color: #b42318;
      font-size: 0.85rem;
      margin-top: 0.5rem;
    }
  `,
];
